import _ from 'lodash';
import React, {createRef, Fragment, RefObject} from 'react';
import block from 'bem-cn-lite';
import {compose} from 'recompose';
import {Spin, StoreBadge} from '@yandex-data-ui/common';
import {Col, Grid, GridJustifyContent, Row} from '@yandex-data-ui/page-constructor';

import {session as safeStorage} from 'utils/safe-storage';
import withEnv, {WithEnvProps} from 'hoc/withEnv';
import withRouter, {WithRouterProps} from 'hoc/withRouter';
import withUser, {WithUserProps} from 'hoc/withUser';
import withIAM, {withIAMProps} from 'hoc/withIAM';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';
import withFeatureFlags, {WithFeatureFlagsProps} from 'hoc/withFeatureFlags';
import withServicesData, {WithServicesDataProps} from 'hoc/withServicesData';
import withSolutionsData, {WithSolutionsDataProps} from 'hoc/withSolutionsData';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

import {Lang} from 'contexts/LangContext';
import metrika from 'counters/metrika';
import getConfig from 'units/navigation/config/footer';
import {
    NavigationItem,
    NavigationStoreBadges,
    NavigationFooterColumnGroup,
} from 'units/navigation/models';

import Logo from 'components/Logo/Logo';
import EnterConsoleLink from 'components/EnterConsoleLink/EnterConsoleLink';
import SubscirptionForm from 'components/forms/SubscriptionForm/SubscriptionForm';
import LangSwitch from 'units/navigation/components/LangSwitch/LangSwitch';
import SimpleNavigationItem from 'units/navigation/components/SimpleNavigationItem/SimpleNavigationItem';
import {checkIsAuthorized, isElementInViewport} from 'utils';
import {getCloudMembership} from './api';

import './Footer.scss';

const b = block('footer');

export type FooterType = 'doc' | 'default';

export interface FooterOwnProps {
    type?: FooterType;
}

export type FooterProps = FooterOwnProps &
    WithEnvProps &
    WithRouterProps &
    WithUserProps &
    withIAMProps &
    WithLocaleProps &
    WithFeatureFlagsProps &
    WithServicesDataProps &
    WithSolutionsDataProps &
    WithTranslationProps;

interface FooterState {
    hasClouds?: boolean;
    hasCloudsLoading?: boolean;
}

const getStorageKey = (uid: string) => {
    return `hasClouds-${uid}`;
};

class Footer extends React.Component<FooterProps, FooterState> {
    ref: RefObject<HTMLDivElement> | null = createRef();
    state: FooterState = {};

    private onScroll = _.debounce(async () => {
        const {hasClouds} = this.state;

        if (
            typeof hasClouds === 'undefined' &&
            this.ref?.current &&
            isElementInViewport(this.ref?.current, 0.5)
        ) {
            const {uid} = this.props.user;
            this.setState({hasCloudsLoading: true});

            const isMemberOfCloud = await getCloudMembership();

            safeStorage.setItem(getStorageKey(uid), String(isMemberOfCloud));
            this.setState({hasClouds: isMemberOfCloud, hasCloudsLoading: false});
        }
    }, 300);

    componentDidMount() {
        const {uid} = this.props.user;
        const hasClouds = safeStorage.getItem(getStorageKey(uid));

        if (hasClouds !== null) {
            this.setState({hasClouds: Boolean(hasClouds)});

            return;
        }

        const {status} = this.props.user;
        const isAuthorized = status && checkIsAuthorized(status);

        if (isAuthorized) {
            window.addEventListener('scroll', this.onScroll);
        } else {
            this.setState({hasClouds: false});
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll);
    }

    render() {
        const {
            i18n,
            env,
            locale,
            featureFlags,
            servicesData,
            solutionsData,
            type = 'default',
        } = this.props;
        const {footer, columns, social, mobileStores} = getConfig({
            env,
            i18n,
            locale,
            featureFlags,
            serviceCategories: servicesData.categories,
            solutionCategories: solutionsData.categories,
        });

        return (
            <footer className={b({type})}>
                {type === 'doc' ? (
                    this.renderUnderline(footer)
                ) : (
                    <Grid>
                        <Row className={b('wrapper')}>
                            {this.renderSubscriptionBlock(social)}
                            {this.renderLinks(columns)}
                            {this.renderStoreBadges(mobileStores, locale.lang)}
                            <Col sizes={{all: 12}}>{this.renderUnderline(footer)}</Col>
                        </Row>
                    </Grid>
                )}
            </footer>
        );
    }

    private renderSubscriptionBlock(social: NavigationItem[]) {
        const socialEl = this.renderSocial(social);

        return (
            <Col sizes={{all: 12, md: 4}}>
                <Row>
                    <Col sizes={{all: 12, sm: 6, md: 12}}>
                        <Logo />
                        <div className={b('tablet', {visible: true})}>{socialEl}</div>
                    </Col>
                    <Col sizes={{all: 12, sm: 6, md: 12}}>
                        {this.renderSubscription()}
                        <div className={b('tablet', {hidden: true})}>{socialEl}</div>
                    </Col>
                </Row>
            </Col>
        );
    }

    private renderSubscriptionAction = () => {
        const {i18nK} = this.props;
        const {hasClouds} = this.state;

        return hasClouds ? (
            <EnterConsoleLink
                text={i18nK('subscribe-in-console-action')}
                context={'footer'}
                path={'/settings'}
                view="outlined"
                size="l"
                className={b('enter-button')}
                onClick={() => metrika.reachGoal('main', metrika.Goal.FOOTER_MNG_SUBSCRIPTIONS)}
            />
        ) : (
            <SubscirptionForm
                agreement={i18nK('subscription-agreement')}
                onSubmit={() => metrika.reachGoal('main', metrika.Goal.FOOTER_SUBSCRIBE)}
            />
        );
    };

    private renderSubscription() {
        const {i18nK} = this.props;
        const {hasClouds, hasCloudsLoading} = this.state;
        const hint = i18nK(`subscription-hint-${hasClouds ? 'login' : 'non-login'}`);
        const loaded = !hasCloudsLoading && typeof hasClouds !== 'undefined';

        return (
            <div
                className={b('subscription', {loading: !loaded})}
                data-qa="subscription"
                ref={this.ref}
            >
                <p className={b('hint')}>{hint}</p>
                {loaded ? (
                    this.renderSubscriptionAction()
                ) : (
                    <div className={b('loader')}>
                        <Spin size="xl" />
                    </div>
                )}
            </div>
        );
    }

    private renderSocial(social: NavigationItem[]) {
        return (
            <div className={b('social')}>
                {social.map((item) => (
                    <SimpleNavigationItem
                        key={item.url}
                        className={b('item', {social: true})}
                        {...item}
                    />
                ))}
            </div>
        );
    }

    private renderLinks(columns: NavigationFooterColumnGroup[][]) {
        return (
            <Fragment>
                {columns.map((groups, index) => (
                    <Col key={index} className={b('column')} sizes={this.getColumnSizes()}>
                        {groups.map((group, groupIndex) =>
                            this.renderGroupLinks(group, groupIndex),
                        )}
                    </Col>
                ))}
            </Fragment>
        );
    }

    private renderStoreBadges(mobileStores: NavigationStoreBadges, lang: Lang) {
        return (
            <Col
                className={b('mobile-store')}
                justifyContent={GridJustifyContent.Center}
                sizes={{all: 12}}
            >
                <Col sizes={{sm: 12, md: 4}}>
                    <h5 className={b('group-title')}>{mobileStores.title}</h5>
                    <div className={b('mobile-store-list')}>
                        {mobileStores.items.map(({platform, url, goal}, index) => (
                            <StoreBadge
                                key={index}
                                className={b('mobile-store-item')}
                                onClick={() => metrika.reachGoal('main', goal)}
                                platform={platform}
                                lang={lang}
                                url={url}
                            />
                        ))}
                    </div>
                </Col>
            </Col>
        );
    }

    private renderGroupLinks({title, items}: NavigationFooterColumnGroup, index: number) {
        return (
            <div className={b('group-wrapper')}>
                <ul key={index} className={b('group')}>
                    <h5 className={b('group-title')}>{title}</h5>
                    {items.map((item) => (
                        <li key={item.title} className={b('item-wrapper')}>
                            <SimpleNavigationItem
                                className={b('item')}
                                {...this.pickFooterItemFields(item)}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    private renderUnderline(footer: NavigationItem[]) {
        const itemClass = b('item', {underline: true});
        const isDoc = this.props.type === 'doc';

        return (
            <div className={b('underline')}>
                <div className={b('underline-links')}>
                    <LangSwitch
                        metrikaGoal={'FOOTER'}
                        className={itemClass}
                        type={'text'}
                        direction={['top-start', 'top', 'top-end']}
                        tooltipClassName={b('lang-tooltip')}
                    />
                    {!isDoc &&
                        footer.map((item) => (
                            <SimpleNavigationItem
                                key={item.title}
                                className={itemClass}
                                {...this.pickFooterItemFields(item)}
                            />
                        ))}
                </div>
                {this.renderCopyRight()}
            </div>
        );
    }

    private getColumnSizes() {
        return {
            all: 6,
            sm: 3,
            md: 2,
        };
    }

    private renderCopyRight() {
        const {i18nK} = this.props;
        const year = new Date().getFullYear();

        return <div className={b('copyright')}>{i18nK('label_copyright', {count: year})}</div>;
    }

    private pickFooterItemFields(item: NavigationItem) {
        return _.pick(item, ['title', 'url', 'blank']);
    }
}

export default compose<FooterProps, FooterOwnProps>(
    withEnv,
    withLocale,
    withRouter,
    withUser,
    withIAM,
    withFeatureFlags,
    withServicesData,
    withSolutionsData,
    withTranslation('footer'),
)(Footer);
